<template>
  <div class="app-shell">
    <header ref="siteHeader" class="site-header">
      <RouterLink class="brand" to="/" aria-label="EP Raggies home">
        <img src="@/assets/img/icons/RassplFish.png" alt="" />
        <span>EP Raggies</span>
      </RouterLink>

      <div class="mobile-header-actions">
        <RouterLink v-if="!isDashboardRoute" class="mobile-login-link" to="/login">Login</RouterLink>
        <button v-else class="mobile-login-link mobile-logout-button" type="button" @click="logout">
          Logout
        </button>

        <button
          class="menu-toggle"
          type="button"
          :aria-expanded="isMenuOpen"
          aria-controls="site-navigation"
          aria-label="Toggle navigation menu"
          @click="isMenuOpen = !isMenuOpen"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <nav
        id="site-navigation"
        class="site-nav"
        :class="{ open: isMenuOpen, 'admin-mobile-menu': isDashboardRoute }"
        aria-label="Main navigation"
      >
        <RouterLink class="public-nav-link" to="/">Home</RouterLink>
        <RouterLink class="public-nav-link" to="/about">About</RouterLink>
        <RouterLink class="public-nav-link" to="/rules">Rules</RouterLink>
        <RouterLink class="public-nav-link" to="/competitions">Comps</RouterLink>
        <RouterLink class="public-nav-link" to="/document-fish">Fish docs</RouterLink>
        <RouterLink class="public-nav-link" to="/fish-calculator">Calculator</RouterLink>
        <RouterLink class="public-nav-link" to="/sponsors">Sponsors</RouterLink>
        <RouterLink class="public-nav-link" to="/announcements">Announcements</RouterLink>
        <RouterLink class="public-nav-link" to="/contact">Socials</RouterLink>
        <RouterLink class="public-nav-link" to="/signup">Signup</RouterLink>
        <RouterLink class="public-nav-link login-link" to="/login">Login</RouterLink>

        <AdminNavigation v-if="isAdminRoute" mobile />
        <button
          v-if="isAdminRoute"
          class="admin-mobile-nav-link admin-mobile-logout"
          type="button"
          @click="logout"
        >
          Logout
        </button>

        <MemberNavigation v-if="isMemberRoute" mobile />
        <button
          v-if="isMemberRoute"
          class="admin-mobile-nav-link admin-mobile-logout"
          type="button"
          @click="logout"
        >
          Logout
        </button>
      </nav>
    </header>

    <RouterView />
    <footer class="site-footer">
      <div class="footer-inner">
        <div v-if="!isDashboardRoute" class="footer-invitation">
          <div>
            <p class="footer-kicker">There’s a place for you on the coast</p>
            <h2>Fish. Compete. Belong.</h2>
            <p>Join the EP Raggies community and make your next catch count.</p>
          </div>
          <RouterLink class="button primary footer-join" to="/signup">Become a member <ArrowUpRight :size="19" aria-hidden="true" /></RouterLink>
        </div>

        <div class="footer-grid">
          <div class="footer-about">
            <RouterLink class="footer-brand" to="/" aria-label="EP Raggies home">
              <img src="@/assets/img/icons/RassplFish.png" alt="" width="50" height="50" />
              <span>EP Raggies<small>Eastern Province · RASSPL Africa</small></span>
            </RouterLink>
            <p>Local waters. Shared passion.<br />Rock and surf angling, competition and community.</p>
            <div class="footer-socials" aria-label="Follow EP Raggies">
              <a href="https://www.facebook.com/share/14iwCDurBfy/" target="_blank" rel="noopener noreferrer" aria-label="EP Raggies on Facebook (opens in a new tab)"><Facebook :size="18" aria-hidden="true" /></a>
              <a href="https://www.instagram.com/ragg.ies8?igsh=MTh4b3VoY29vbnMzdw==" target="_blank" rel="noopener noreferrer" aria-label="EP Raggies on Instagram (opens in a new tab)"><Instagram :size="18" aria-hidden="true" /></a>
              <a href="mailto:epraggies1@gmail.com" aria-label="Email EP Raggies"><Mail :size="18" aria-hidden="true" /></a>
            </div>
          </div>

          <nav class="footer-links" aria-label="Explore EP Raggies">
            <h3>Explore</h3>
            <RouterLink to="/competitions">Competitions</RouterLink>
            <RouterLink to="/announcements">Latest news</RouterLink>
            <RouterLink to="/rules">Competition rules</RouterLink>
            <RouterLink to="/sponsors">Our sponsors</RouterLink>
          </nav>

          <nav class="footer-links" aria-label="Member links">
            <h3>Your membership</h3>
            <RouterLink to="/signup">Sign up</RouterLink>
            <RouterLink to="/login">Member login</RouterLink>
            <RouterLink to="/document-fish">Catch photo guide</RouterLink>
            <RouterLink to="/fish-calculator">Fish weight calculator</RouterLink>
          </nav>

          <div class="footer-connect">
            <h3>Let’s talk fishing</h3>
            <p>Membership, competitions or a quick question? Get in touch with the team.</p>
            <a class="footer-email" href="mailto:epraggies1@gmail.com"><Mail :size="17" aria-hidden="true" /><span>epraggies1@gmail.com</span></a>
            <RouterLink class="footer-contact-link" to="/contact">Contact &amp; socials <ArrowUpRight :size="16" aria-hidden="true" /></RouterLink>
          </div>
        </div>

        <div class="footer-bottom">
          <p>© {{ footerYear }} {{ operatorName }}. All rights reserved.</p>
          <nav class="footer-utilities" aria-label="Website information">
            <RouterLink to="/policies">Policies</RouterLink>
            <button type="button" @click="showCookiePreferences">Cookie settings</button>
            <button class="footer-top-link" type="button" @click="scrollToTop">Back to top <ArrowUp :size="15" aria-hidden="true" /></button>
          </nav>
        </div>
      </div>
    </footer>
    <section v-if="showCookieConsent" ref="cookieBanner" class="cookie-consent" aria-label="Cookie preferences" tabindex="-1">
      <div>
        <strong>Cookie preferences</strong>
        <p>
          Essential cookies keep the website working. Optional analytics helps us understand visits
          and stays off unless you accept. You can change your choice in Cookie settings at any time.
          <RouterLink to="/policies/cookie-policy">Read our Cookie Policy</RouterLink> and
          <RouterLink to="/policies/privacy-policy">Privacy Policy</RouterLink>.
        </p>
      </div>
      <div class="cookie-consent-actions">
        <button type="button" class="cookie-consent-secondary" @click="declineAnalytics">
          Essential only
        </button>
        <button type="button" class="cookie-consent-primary" @click="allowAnalytics">
          Accept analytics
        </button>
        <button v-if="hasAnalyticsChoice" type="button" class="cookie-consent-secondary" @click="closeCookiePreferences">Close settings</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import MemberNavigation from '@/components/MemberNavigation.vue';
import AdminNavigation from './components/AdminNavigation.vue';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { logoutUser } from './auth';
import { OPERATOR_NAME as operatorName } from './data/policies';
import { ArrowUp, ArrowUpRight, Facebook, Instagram, Mail } from 'lucide-vue-next';
import {
  acceptAnalytics,
  hasStoredAnalyticsChoice,
  rejectAnalytics,
} from './analytics';

const route = useRoute();
const router = useRouter();
const footerYear = new Date().getFullYear();
const isMenuOpen = ref(false);
const hasAnalyticsChoice = ref(hasStoredAnalyticsChoice());
const cookiePreferencesOpen = ref(false);
const cookieBanner = ref(null);
const siteHeader = ref(null);
let headerResizeObserver;
let cookieSettingsTrigger;
const isAdminRoute = computed(() => route.path.startsWith('/admin'));
const isMemberRoute = computed(() => route.path.startsWith('/member'));
const isDashboardRoute = computed(() => isAdminRoute.value || isMemberRoute.value);
const showCookieConsent = computed(() => (
  cookiePreferencesOpen.value || (!hasAnalyticsChoice.value && !isDashboardRoute.value)
));

async function showCookiePreferences() {
  cookieSettingsTrigger = document.activeElement;
  cookiePreferencesOpen.value = true;
  await nextTick();
  cookieBanner.value?.focus();
}

function closeCookiePreferences() {
  cookiePreferencesOpen.value = false;
  cookieSettingsTrigger?.focus();
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}

async function logout() {
  await logoutUser();
  router.push({ name: 'home' });
}

function allowAnalytics() {
  acceptAnalytics(route);
  hasAnalyticsChoice.value = true;
  closeCookiePreferences();
}

function declineAnalytics() {
  rejectAnalytics();
  hasAnalyticsChoice.value = true;
  closeCookiePreferences();
}

function refreshAnalyticsChoice() {
  hasAnalyticsChoice.value = hasStoredAnalyticsChoice();
}

onMounted(() => {
  headerResizeObserver = new ResizeObserver(() => {
    const header = siteHeader.value;
    if (header) header.style.setProperty('--site-header-height', `${header.getBoundingClientRect().height}px`);
  });
  if (siteHeader.value) headerResizeObserver.observe(siteHeader.value);
  window.addEventListener('rasspl-analytics-consent-changed', refreshAnalyticsChoice);
  window.addEventListener('rasspl-open-cookie-preferences', showCookiePreferences);
  window.addEventListener('storage', refreshAnalyticsChoice);
});

onUnmounted(() => {
  headerResizeObserver?.disconnect();
  window.removeEventListener('rasspl-analytics-consent-changed', refreshAnalyticsChoice);
  window.removeEventListener('rasspl-open-cookie-preferences', showCookiePreferences);
  window.removeEventListener('storage', refreshAnalyticsChoice);
});

watch(
  () => route.fullPath,
  () => {
    isMenuOpen.value = false;
    refreshAnalyticsChoice();
  }
);
</script>
